import { Button } from '@sunwebgroup/uds-ui/button'
import { Icon } from '@sunwebgroup/uds-ui/icon'
import { Sheet } from '@sunwebgroup/uds-ui/sheet'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import cn from 'classnames'
import { isEmpty } from 'ramda'
import { use, useState } from 'react'
import { useIntl } from '@/intl'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { busesSelector } from '@/scopes/prices/components/selectors'
import { BusCard } from '@/scopes/prices/components/TransportSelector/BusSelector/BusCard'
import { InboundTitle, OutboundTitle } from '@/scopes/prices/components/TransportSelector/BusSelector/Headers'
import { createBusComponentInteractionEvent } from '@/scopes/prices/domain/analytics'
import { PricesActionsContext } from '@/scopes/prices/domain/PricesActionsContext'
import { usePricesSelector } from '@/scopes/prices/infrastructure/usePricesSelector'
import styles from './BusSelectorMobile.module.css'

const STEPS = {
  selectDeparture: 'selectDeparture',
  selectReturn: 'selectReturn',
}

export function BusSelectorMobile() {
  const intl = useIntl()
  const {
    setOutboundBusId,
    setInboundBusId,
  } = use(PricesActionsContext)

  const { outboundBuses, inboundBuses, selectedOutboundBusId, selectedInboundBusId, outboundBus, inboundBus }
    = usePricesSelector(busesSelector)

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [step, setStep] = useState<string | null>(null)
  const { sendEvent } = useAnalytics()
  const sheetTitle
    = step === STEPS.selectDeparture
      ? `${intl.formatMessage({ id: 'prices_transport_selector_bus-selector_select_your_bus_outward' })}`
      : `${intl.formatMessage({ id: 'prices_transport_selector_bus-selector_select_your_bus_inward' })}`
  if (isEmpty(outboundBuses) || isEmpty(inboundBuses))
    return null

  const handleOpenModal = () => {
    setIsModalOpen(true)
    setStep(STEPS.selectDeparture)
    sendAnalyticsEvent('outbound')
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setStep(null)
  }

  function changeStep(text: string) {
    setStep(text)
    sendAnalyticsEvent(text === STEPS.selectDeparture ? 'outbound' : 'inbound')
  }

  function sendAnalyticsEvent(type: string) {
    sendEvent(createBusComponentInteractionEvent({ type, result: 'Change Bus' }))
  }

  const selectOutboundBus = (id: string) => {
    setOutboundBusId(id)
  }

  const selectInboundBus = (id: string) => {
    setInboundBusId(id)
  }

  return (
    <>
      <div className={styles.busSelectorContainer}>
        <Typography className={styles.busSelectorTitle} variant="display-f-regular">
          {intl.formatMessage({ id: 'prices_transport_selector_bus-selector_title' })}
        </Typography>
        <div className={styles.wrapper} id="BusSelector">
          <div className={styles.section}>
            <OutboundTitle />
            {outboundBus && <BusCard {...outboundBus} isSelected noActiveState stationName={outboundBus.departureStationName} cityName={outboundBus.departureCityName} date={outboundBus.departureDate} />}
          </div>
          <div className={styles.section}>
            <InboundTitle />
            {inboundBus && <BusCard {...inboundBus} isSelected noActiveState stationName={inboundBus.arrivalStationName} cityName={inboundBus.arrivalCityName} />}
          </div>
          <Button variant="secondary" onClick={handleOpenModal}>
            {intl.formatMessage({
              id: 'prices_transport_selector_bus-selector-mobile_button',
            })}
          </Button>
        </div>
        <Sheet.Root open={isModalOpen} onOpenChange={handleCloseModal}>
          <Sheet.Body side="right">
            <div className={styles.sheetCustomHeader}>
              <div className={styles.headerCell}>
                {step === STEPS.selectReturn && (
                  <button type="button" className={styles.returnWrapper} onClick={() => changeStep(STEPS.selectDeparture)}>
                    <Icon glyph="ChevronLeft" size={20}></Icon>
                    <Typography variant="body-m-bold-tight">
                      {intl.formatMessage({ id: 'prices_transport_selector_bus-selector_outward' })}
                    </Typography>
                  </button>
                )}
              </div>
              <div className={cn(styles.headerCell, styles.headerCellCenter)}>
                <Typography variant="body-m-bold-tight">
                  {step === STEPS.selectDeparture
                    ? intl.formatMessage({ id: 'prices_transport_selector_bus-selector_outward-bus' })
                    : intl.formatMessage({ id: 'prices_transport_selector_bus-selector-inward-bus' })}
                </Typography>
              </div>
              <div className={cn(styles.headerCell, styles.headerCellEnd)}>
                <Button variant="transparentPrimary" onClick={handleCloseModal}>
                  {intl.formatMessage({ id: 'prices_transport_selector_bus-selector_cancel' })}
                </Button>
              </div>
            </div>
            <Sheet.Content className={styles.sheetContent}>
              <Typography className={styles.sheetTitle} variant="body-m-regular-snug">{sheetTitle}</Typography>
              {step === STEPS.selectDeparture && (
                <div>
                  {outboundBuses.map(bus => (
                    <div className={styles.busCardWrapper} key={bus.id}>
                      <BusCard
                        {...bus}
                        cityName={bus.departureCityName}
                        stationName={bus.departureStationName}
                        date={bus.departureDate}
                        setSelected={selectOutboundBus}
                        isSelected={selectedOutboundBusId === bus.id}
                      />
                    </div>
                  ))}
                </div>
              )}
              {step === STEPS.selectReturn && (
                <div>
                  {inboundBuses.map(bus => (
                    <div className={styles.busCardWrapper} key={bus.id}>
                      <BusCard
                        {...bus}
                        cityName={bus.arrivalCityName}
                        stationName={bus.arrivalStationName}
                        setSelected={selectInboundBus}
                        isSelected={selectedInboundBusId === bus.id}
                      />
                    </div>
                  ))}
                </div>
              )}
              <div className={styles.buttonWrapper}>
                {step === STEPS.selectDeparture && (
                  <Button onClick={() => changeStep(STEPS.selectReturn)} fullWidth>
                    {intl.formatMessage({ id: 'prices_transport_selector_bus-selector_save_and_continue' })}
                  </Button>
                )}
                {step === STEPS.selectReturn && (
                  <Button onClick={handleCloseModal} fullWidth>
                    {intl.formatMessage({ id: 'prices_transport_selector_bus-selector-save' })}
                  </Button>
                )}
              </div>
            </Sheet.Content>
          </Sheet.Body>
        </Sheet.Root>
      </div>
    </>
  )
}
